import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Form } from '@/components/ui/form';
import { displayToastSuccess } from '@/helpers/display-toast';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { useDeleteSeniorities } from '@/modules/settings/hooks/seniorities/use-delete-seniorities';
import { Seniority, SeniorityFormData } from '@/modules/settings/types/seniority';

interface DeleteSeniorityModalProps {
  seniority: SeniorityFormData | null;
  allSeniorities: Seniority[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Special value for "No seniority" option
const NO_SENIORITY_VALUE = -1;

// Dynamic schema based on whether seniority has members
// Note: SingleSelect returns string values, so we accept string and transform to number
const createDeleteSchema = (hasMembers: boolean) =>
  z.object({
    replacementSeniorityId: hasMembers
      ? z
          .string({
            required_error:
              'authenticated.settings:pages.seniorities.deleteModal.replacementRequired',
          })
          .transform(val => parseInt(val, 10))
      : z
          .string()
          .optional()
          .transform(val => (val ? parseInt(val, 10) : undefined)),
  });

type DeleteFormData = z.infer<ReturnType<typeof createDeleteSchema>>;

/**
 * Modal for deleting a seniority with optional member transfer
 * If seniority has members, user must select a replacement seniority
 *
 * @example
 * ```tsx
 * <DeleteSeniorityModal
 *   seniority={selectedSeniority}
 *   allSeniorities={seniorities}
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   onSuccess={() => {
 *     toast.success('Seniority deleted');
 *     setIsModalOpen(false);
 *   }}
 * />
 * ```
 */
export function DeleteSeniorityModal({
  seniority,
  allSeniorities,
  open,
  onClose,
  onSuccess,
}: DeleteSeniorityModalProps) {
  const { t } = useTranslation('authenticated.settings');
  const deleteSeniorityMutation = useDeleteSeniorities();

  const hasMembers = seniority ? seniority.memberCount > 0 : false;

  const form = useForm<DeleteFormData>({
    resolver: zodResolver(createDeleteSchema(hasMembers)),
    mode: 'onChange',
    defaultValues: {
      replacementSeniorityId: undefined,
    },
  });

  // Build replacement options with "No seniority" as first option
  const replacementOptions = [
    {
      value: NO_SENIORITY_VALUE.toString(),
      label: t('pages.seniorities.deleteModal.noSeniority'),
    },
    ...allSeniorities
      .filter(sen => sen.id !== seniority?.seniorityId)
      .map(sen => ({
        value: sen.id.toString(),
        label: sen.name,
      })),
  ];

  const handleDelete = async (closeModal: () => void) => {
    if (!seniority) return;

    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();
    const seniorityName = seniority.name;

    // Build request payload conditionally
    const payload: {
      seniorityId: number;
      replacementSeniorityId?: number;
    } = {
      seniorityId: seniority.seniorityId,
    };

    // form.getValues() returns the raw string value (pre-transform)
    // So we compare against the string representation of NO_SENIORITY_VALUE
    const rawValue = formData.replacementSeniorityId;
    if (rawValue !== undefined && rawValue !== null) {
      const stringValue = String(rawValue);
      if (stringValue !== NO_SENIORITY_VALUE.toString()) {
        payload.replacementSeniorityId = parseInt(stringValue, 10);
      }
    }

    try {
      await deleteSeniorityMutation.mutateAsync(payload);
      form.reset();
      closeModal();
      displayToastSuccess(
        t('pages.seniorities.deleteModal.successDescription', {
          name: seniorityName,
        })
      );
      onSuccess?.();
    } catch {
      // Error handled by mutation onError
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const replacementSeniorityId = form.watch('replacementSeniorityId');
  const isFormValid = !hasMembers || !!replacementSeniorityId;

  const messageContent = hasMembers ? (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        <Trans
          i18nKey="authenticated.settings:pages.seniorities.deleteModal.messageWithMembers"
          values={{ count: seniority?.memberCount }}
          components={{ strong: <strong className="font-semibold text-foreground" /> }}
        />
      </div>
    </div>
  ) : (
    <div className="text-sm text-muted-foreground">
      {t('pages.seniorities.deleteModal.messageNoMembers')}
    </div>
  );

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={handleClose}
      title={t('pages.seniorities.deleteModal.title')}
      message={messageContent}
      confirmText={
        deleteSeniorityMutation.isPending
          ? t('pages.seniorities.deleteModal.deleting')
          : t('pages.seniorities.deleteModal.delete')
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteSeniorityMutation.isPending}
      disabled={!isFormValid}
    >
      {hasMembers && (
        <Form {...form}>
          <form className="space-y-4">
            <SingleSelect
              name="replacementSeniorityId"
              control={form.control}
              options={replacementOptions}
              label={t('pages.seniorities.deleteModal.replacementLabel')}
              placeholder={t('pages.seniorities.deleteModal.replacementPlaceholder')}
              searchable={true}
              isClearSelectionEnabled={true}
            />
          </form>
        </Form>
      )}
    </ConfirmationModal>
  );
}
